<template>
    <div class="video-container">
        <CRow class="video-list">
            <template v-for="tab in tabs">
                <CCol class="video-container__player-wrapper" v-bind="columnSize" :key="tab + 'player'" v-if="shownTabs.indexOf(tab) !== -1" v-show="isVideoActive(tab)">
                    <template v-if="isVideoRender(tab)">
                        <RowingVideoPlayer
                            :videoFileId="getVideoFileId(tab)"
                            :posterFileId="getPosterFileId(tab)"
                            :videoFramesSchema="getVideoFramesSchema(tab)"
                            :strokes="getStrokes(tab)"
                            :reportSrcVideoFramesSchema="vPR.reportSrcVideoFramesSchema"
                            :reportSrcVideoInitialTime="reportSrcVideoTime"
                            :doPreload="doPreload && tab === 'seat1' || isActiveCompareTab ? 'auto' : 'none'"
                            :playersSyncState="playersSyncState"
                            :needSync="isActiveCompareTab"
                            v-on:timeSet="setReportSrcVideoTimeByPlayer"
                            v-on:playersSync="syncPlayers"
                            v-on:playerTime="(time) => {setPlayerTime(tab, time)}"
                            :key="getVideoFileId(tab)"
                        />
                        <RowingPdf
                            v-if="tab.slice(0, 4) === 'seat' || isCompareErgTab(tab)"
                            :training="getTraining(tab)"
                            :activeTab="activeTab"
                            :vPR="getVideoResult(tab)"
                            :activeSeatNum="getActiveSeatNum(tab)"
                            :trainingUrl="getTrainingUrl(tab)"
                            :trainingParamsArrForUrl="trainingParamsArrForUrl"
                            :feedType="feedType"
                        />
                    </template>
                    <template v-else-if="tab === 'source' && vPR.reportSrcVideo">
                        <RowingVideoPlayer
                            :reportPcVideoFileId="vPR.reportSrcVideo.id"
                            :videoFileId="vPR.reportSrcVideo.id"
                            :posterFileId="vPR.reportSrcVideoPoster ? vPR.reportSrcVideoPoster.id : null"
                            :videoFramesSchema="vPR.reportSrcVideoFramesSchema"
                            :reportSrcVideoFramesSchema="vPR.reportSrcVideoFramesSchema"
                            :strokes="JSON.parse(vPR['strokes1'])"
                            :doPreload="doPreload ? 'auto' : 'none'"
                            :reportSrcVideoInitialTime="reportSrcVideoTime"
                            v-on:timeSet="setReportSrcVideoTimeByPlayer"
                            :key="'reportSrcVideo'+vPR.reportSrcVideo.id"
                        />
                    </template>
                    <template v-else-if="tab === 'compareErg' && !isCompareErgAvailable">
                        <div class="empty-video">
                            <p>
                                <strong>Select video to compare</strong><br>
                                Note: Name athletes in the "Who is on video" field for technique tracking and
                                cross-comparisons with other athletes over time. In cases where the athlete's name
                                is not labeled, the field displays 'NoName'.
                            </p>
                        </div>
                    </template>
                </CCol>
            </template>
        </CRow>
    </div>
</template>

<script>
    import RowingVideoPlayer from "@/components/RowingVideoPlayer.vue";
    import RowingPdf from "@/components/RowingPdf.vue";
    import {getTimeDecimalFromTrainingParams, getTrainingParamsFromUrlPartOrArr, generateTrainingUrl} from "@/scripts/training-utils";
    import trainingParticipantsAndPrivacyMixin from "@/mixins/training-participants-and-privacy";
    import fileUtils from "@/mixins/file-utils";

    export default {
        components: {
            RowingVideoPlayer,
            RowingPdf
        },
        props: {
            training: Object,
            compareTraining: Object,
            doPreload: Boolean,
            feedType: String,
            tabs: Array,
            activeTab: String,
            activeSeatNum: [String, Number],
            trainingUrl: String,
            trainingParamsArrForUrl: Array,
            trainingParams: Object,
            myUser: Object,
        },
        mixins: [trainingParticipantsAndPrivacyMixin, fileUtils],
        data() {
            return {
                collapseDownloads: false,
                reportSrcVideoTime: this.feedType === 'training' ? getTimeDecimalFromTrainingParams(getTrainingParamsFromUrlPartOrArr(this.$route.params.trainingParams)) : null,
                playersReportSrcVideoTime: {},
                playersSyncState: {},
                shownTabs: [],
                playersTime: {},
            }
        },
        methods: {
            setReportSrcVideoTimeByPlayer(time) {
                this.reportSrcVideoTime = time
                this.playersReportSrcVideoTime[this.activeTab] = time
            },
            syncPlayers(state) {
                if (!(this.isActiveCompareTab && (!this.playersSyncState || this.playersSyncState.code !== state.code))) {
                    return;
                }
                this.playersSyncState = state;
            },
            onActiveTabChanged() {
                this.reportSrcVideoTime = this.playersReportSrcVideoTime[this.activeTab] || null

                if (!this.shownTabs.includes(this.activeTab))
                    this.shownTabs.push(this.activeTab)
                if (this.isActiveCompareTab) {
                    this.tabs.forEach(tab => {
                        if (tab.slice(0, 4) === 'seat' && !this.shownTabs.includes(tab)) {
                            this.shownTabs.push(tab);
                        }
                    });
                }
            },
            isVideoActive(tab) {
                return tab === this.activeTab
                    || (this.activeTab === 'compare' && tab.slice(0, 4) === 'seat')
                    || (this.activeTab === 'compareErg' && ['seat1', 'compareErg'].includes(tab));
            },
            isVideoRender(tab) {
                return (tab.slice(0, 4) === 'seat' && this.vPR['reportPcVideo'+tab[4]])
                    || (tab === 'compareErg' && this.isCompareErgAvailable);
            },
            isCompareErgTab(tab) {
                return tab === 'compareErg' && this.isCompareErgAvailable;
            },
            getTraining(tab) {
                return this.isCompareErgTab(tab) ? this.compareTraining : this.training;
            },
            getTrainingUrl(tab) {
                return this.isCompareErgTab(tab)
                    ? generateTrainingUrl(this.compareTraining, this.myUser, this.activeTab)
                    : this.trainingUrl;
            },
            getActiveSeatNum(tab) {
                return this.isCompareErgTab(tab) ? '1' : tab[4];
            },
            getVideoResult(tab) {
                return this.isCompareErgTab(tab) ? this.compareVPR : this.vPR;
            },
            getSeatNum(tab) {
                return tab === 'compareErg' ? '1' : tab[4];
            },
            getVideoFileId(tab) {
                const type = this.isActiveCompareTab ? 'reportInstaVideo' : 'reportPcVideo';
                const videoResult = this.getVideoResult(tab);
                const seatNum = this.getSeatNum(tab);
                return videoResult[type + seatNum] ? videoResult[type + seatNum].id : '';
            },
            getPosterFileId(tab) {
                const videoResult = this.getVideoResult(tab);
                const seatNum = this.getSeatNum(tab);
                return videoResult['reportPcVideo'+ seatNum + 'Poster'].id;
            },
            getVideoFramesSchema(tab) {
                const videoResult = this.getVideoResult(tab);
                const seatNum = this.getSeatNum(tab);
                return videoResult['reportPcVideo'+ seatNum + 'FramesSchema'];
            },
            getStrokes(tab) {
                const videoResult = this.getVideoResult(tab);
                const seatNum = this.getSeatNum(tab);
                return JSON.parse(videoResult['strokes'+ seatNum]);
            },
            setPlayerTime(tab, time) {
                this.playersTime[tab] = time;
            }
        },
        computed: {
            video() {
                return this.training.videos[0]
            },
            vPR() {
                return this.video.videoProcessingResult
            },
            compareVPR() {
                return this.compareTraining.videos[0] && this.compareTraining.videos[0].videoProcessingResult
                    ? this.compareTraining.videos[0].videoProcessingResult : null;
            },
            columnSize() {
                return this.isActiveCompareTab ? {'lg': 6, 'col':12} : {'col':12};
            },
            isCompareErgAvailable() {
                return this.compareTraining !== null
                    && typeof this.compareTraining === 'object'
                    && Object.keys(this.compareTraining).length !== 0;
            },
            isActiveCompareTab() {
                return ['compare', 'compareErg'].includes(this.activeTab);
            },
        },
        watch: {
            activeTab: function () {
                this.onActiveTabChanged();
            },
        },
        created() {
            this.onActiveTabChanged();
            this.playersReportSrcVideoTime[this.activeTab] = parseFloat(this.trainingParams['time']) || null
        }
    }
</script>

<style lang="scss">
.empty-video {
    background-color: #f3fafc;
    text-align: center;
    display: grid;
    align-items: center;
    aspect-ratio: 1 / 1;
}
@media (max-width: 992px) {
    .video-container .video-container__player-wrapper {
        padding-left: 0;
        padding-right: 0;
    }
}
</style>